/**
 * The production {@link RouteRenderer}: a headless-Playwright renderer that
 * drives the JUST-BUILT SPA from `dist/` and returns each route's rendered
 * `#app` body.
 *
 * Design choices (see `bodyPrerender.ts` for the why):
 *   - **Renders the PRODUCTION bundle**, so `import.meta.env.DEV` is already
 *     `false` and the dev-only fabrication gates render their honest empty state
 *     (A-34 safety by construction — no separate SSR bundle to leak defaults).
 *   - **Playwright is dynamically imported and OPTIONAL.** If it (or a browser)
 *     is unavailable, the factory returns `null` and the caller no-ops (the head
 *     + JSON-LD emit is untouched, so the build never breaks on missing infra).
 *     Playwright is already a universal devDependency of the DCS site fleet
 *     (snapshot capture), so this activates on a cms bump with no per-site edit.
 *   - **Offline + deterministic.** A tiny built-in static server serves `dist/`;
 *     every EXTERNAL request (CDN images, App Insights, the site-auth probe) is
 *     aborted. The DOM still carries those `<img>`/`<link>` tags (crawler-visible)
 *     while the render stays fast and free of expected offline network noise.
 *   - **Crash-only fail-loud.** Only an uncaught page exception or a failed mount
 *     is reported as a fatal error; benign offline console errors are not. The
 *     caller turns a fatal error into a red build.
 */

import http from 'node:http'
import fs from 'node:fs'
import path from 'node:path'
import type { AddressInfo } from 'node:net'
import type { RenderedRoute, RouteRenderer } from './bodyPrerender'

/**
 * The URL the renderer navigates to for a route (C-1553).
 *
 * The prerender drives the site's PRODUCTION bundle, connection string and all.
 * Every external request is aborted by the route filter below, but Chromium's
 * `navigator.sendBeacon` is not routed through `page.route`, so the telemetry
 * SDK's unload flush still reached the customer's production App Insights
 * component: measured 2026-09-08 on ai-boogie-babies (22 rows in 30 d) and
 * ai-mi-handyman (33), every one `HeadlessChrome` with `Target 127.0.0.1:<port>`,
 * one burst per deploy. Marking the navigation with the C-614 synthetic-capture
 * parameter makes the site's telemetry boot decline BEFORE the SDK exists, which
 * is the same contract the snapshot capture already honours. The marker is a
 * query parameter the head emitter never reads (canonical/og:url come from the
 * site config, not `location`), so the baked head is unchanged.
 */
export function prerenderNavigationUrl(origin: string, routePath: string): string {
  const url = new URL(routePath, origin)
  url.searchParams.set('dcs-no-telemetry', '')
  return url.toString()
}

/** A started static server plus its `http://127.0.0.1:<port>` origin. */
interface StaticServer {
  origin: string
  close: () => Promise<void>
}

const CONTENT_TYPES: Record<string, string> = {
  '.html': 'text/html; charset=utf-8',
  '.js': 'text/javascript; charset=utf-8',
  '.mjs': 'text/javascript; charset=utf-8',
  '.css': 'text/css; charset=utf-8',
  '.json': 'application/json; charset=utf-8',
  '.svg': 'image/svg+xml',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
  '.gif': 'image/gif',
  '.ico': 'image/x-icon',
  '.woff': 'font/woff',
  '.woff2': 'font/woff2',
  '.ttf': 'font/ttf',
  '.txt': 'text/plain; charset=utf-8',
  '.xml': 'application/xml; charset=utf-8',
  '.map': 'application/json; charset=utf-8',
}

/**
 * Serve `dist/` over loopback with directory-index resolution
 * (`/services` → `/services/index.html`) and an SPA fallback to the root
 * `index.html` for any path with no matching file/dir. Bound to `127.0.0.1:0`
 * so the OS assigns a free port; nothing is exposed off-box.
 */
async function startStaticServer(outDir: string): Promise<StaticServer> {
  const server = http.createServer((req, res) => {
    let pathname = '/'
    try {
      pathname = decodeURIComponent(new URL(req.url ?? '/', 'http://127.0.0.1').pathname)
    } catch {
      pathname = '/'
    }

    // Resolve within outDir; reject path traversal.
    const resolved = path.resolve(outDir, '.' + pathname)
    if (resolved !== outDir && !resolved.startsWith(outDir + path.sep)) {
      res.writeHead(403)
      res.end()
      return
    }

    let filePath = resolved
    try {
      const stat = fs.statSync(filePath)
      if (stat.isDirectory()) filePath = path.join(filePath, 'index.html')
    } catch {
      // Missing path: a request for an asset (has an extension) is a genuine 404;
      // a route-shaped request falls back to the SPA shell.
      if (path.extname(pathname)) {
        res.writeHead(404)
        res.end()
        return
      }
      filePath = path.join(outDir, 'index.html')
    }

    fs.readFile(filePath, (err, buf) => {
      if (err) {
        res.writeHead(404)
        res.end()
        return
      }
      res.writeHead(200, {
        'Content-Type': CONTENT_TYPES[path.extname(filePath).toLowerCase()] ?? 'application/octet-stream',
      })
      res.end(buf)
    })
  })

  await new Promise<void>((resolve) => server.listen(0, '127.0.0.1', resolve))
  const addr = server.address() as AddressInfo
  const origin = `http://127.0.0.1:${addr.port}`

  return {
    origin,
    close: () =>
      new Promise<void>((resolve) => {
        server.close(() => resolve())
      }),
  }
}

/** Options for {@link createPlaywrightRenderer}. */
export interface PlaywrightRendererOptions {
  /** Absolute path to the built SPA output directory (`dist`). */
  outDir: string
  /** Per-route navigation + settle budget (ms). Default 30000 nav, 15000 mount. */
  navTimeoutMs?: number
  mountTimeoutMs?: number
  /** Extra quiet settle after mount for post-render microtasks (ms). Default 250. */
  settleMs?: number
  debug?: boolean
}

/** A ready renderer plus a `close()` that tears down the browser + server. */
export interface PlaywrightRendererHandle {
  renderer: RouteRenderer
  close: () => Promise<void>
}

// Minimal structural types for the OPTIONAL, dynamically-imported `playwright`
// surface — avoids a hard type/dependency on the package (it is not a cms
// devDependency; it resolves from the consuming site at build time).
interface PwConsoleMessage {
  type(): string
  text(): string
}
interface PwPage {
  on(event: 'pageerror', cb: (err: Error) => void): void
  on(event: 'console', cb: (msg: PwConsoleMessage) => void): void
  route(url: string, handler: (route: PwRoute) => unknown): Promise<void>
  goto(url: string, opts: { waitUntil: string; timeout: number }): Promise<unknown>
  waitForFunction(fn: () => unknown, arg?: unknown, opts?: { timeout: number }): Promise<unknown>
  waitForTimeout(ms: number): Promise<void>
  $eval(sel: string, fn: (el: Element) => string): Promise<string>
  evaluate<T>(fn: () => T): Promise<T>
  close(): Promise<void>
}
interface PwRoute {
  request(): { url(): string }
  abort(): Promise<void>
  continue(): Promise<void>
}
interface PwContext {
  newPage(): Promise<PwPage>
  close(): Promise<void>
}
interface PwBrowser {
  newContext(opts?: unknown): Promise<PwContext>
  close(): Promise<void>
}

/**
 * Build the headless-Playwright renderer, or return `null` when Playwright / a
 * browser is unavailable (the caller then no-ops — head + JSON-LD are already
 * emitted, so the build is unaffected). Never throws on missing infra.
 */
export async function createPlaywrightRenderer(
  options: PlaywrightRendererOptions
): Promise<PlaywrightRendererHandle | null> {
  const {
    outDir,
    navTimeoutMs = 30000,
    mountTimeoutMs = 15000,
    settleMs = 250,
    debug = false,
  } = options

  // Optional dependency: resolve `playwright` from the consuming site. The
  // computed specifier keeps the bundler from trying to resolve it at build time.
  let chromium: { launch(opts?: unknown): Promise<PwBrowser> } | undefined
  try {
    const specifier = 'playwright'
    const pw = (await import(/* @vite-ignore */ specifier)) as {
      chromium?: { launch(opts?: unknown): Promise<PwBrowser> }
    }
    chromium = pw.chromium
  } catch {
    console.warn(
      '[dcs-seo] body-prerender: `playwright` is not installed; skipping body ' +
        'prerender (head + JSON-LD still emitted). `pnpm add -D playwright` to enable.'
    )
    return null
  }
  if (!chromium) {
    console.warn('[dcs-seo] body-prerender: playwright has no chromium export; skipping.')
    return null
  }

  let browser: PwBrowser
  try {
    browser = await chromium.launch({ headless: true })
  } catch (err) {
    console.warn(
      '[dcs-seo] body-prerender: failed to launch Chromium (run ' +
        '`npx playwright install chromium`); skipping body prerender:',
      err instanceof Error ? err.message : err
    )
    return null
  }

  // From here on a LIVE Chromium process is owned by this function, and the only
  // thing that can ever close it is the `close()` returned at the bottom. If any
  // step between the launch and that return throws, the caller receives a
  // rejection and no handle — so nothing is left holding a reference to the
  // browser, and it survives as an orphan process plus the open handles that
  // keep its host alive (a `vite build`, or a vitest worker). C-488: unwind what
  // we managed to construct, in reverse, before letting the error out.
  let staticServer: StaticServer | undefined
  let context: PwContext
  try {
    staticServer = await startStaticServer(outDir)
    context = await browser.newContext({ reducedMotion: 'reduce' })
  } catch (err) {
    await staticServer?.close().catch(() => {})
    await browser.close().catch(() => {})
    throw err
  }
  const server = staticServer

  const renderer: RouteRenderer = {
    async renderRoute(routePath: string): Promise<RenderedRoute> {
      const page = await context.newPage()
      const errors: string[] = []
      page.on('pageerror', (err) => errors.push(`pageerror: ${err.message}`))
      if (debug) {
        page.on('console', (msg) => {
          if (msg.type() === 'error') console.log(`[dcs-seo]   (console) ${msg.text()}`)
        })
      }

      // Keep the render offline + fast: serve our own origin, abort everything
      // else. The DOM keeps external <img>/<link> tags regardless. Compare the
      // parsed origin, not a string prefix — `http://127.0.0.1:<port>@evil.com/…`
      // starts with our origin but resolves to evil.com. (Perf/noise gate, not a
      // security boundary — the trust boundary is that we run the site's own
      // production bundle.)
      await page.route('**/*', (route) => {
        let sameOrigin = false
        try {
          sameOrigin = new URL(route.request().url()).origin === server.origin
        } catch {
          sameOrigin = false
        }
        // Swallow the continue/abort rejection that races page.close ("Route is
        // already handled" / target closed) — an unhandled rejection would crash
        // the vite process under Node's throw-on-unhandled default.
        if (sameOrigin) {
          route.continue().catch(() => {})
        } else {
          route.abort().catch(() => {})
        }
      })

      try {
        await page.goto(prerenderNavigationUrl(server.origin, routePath), {
          waitUntil: 'load',
          timeout: navTimeoutMs,
        })
        // The app mounts into #app; wait until it has real children (a failed
        // mount / crash never satisfies this → timeout → fatal error below).
        await page.waitForFunction(
          () => {
            const el = document.querySelector('#app')
            return !!el && el.children.length > 0
          },
          undefined,
          { timeout: mountTimeoutMs }
        )
        if (settleMs > 0) await page.waitForTimeout(settleMs)
        const bodyHtml = await page.$eval('#app', (el) => el.innerHTML)
        // P1: read the POST-MOUNT <head> from the same render. This is what a
        // JS-rendering crawler (and every human) sees, and it is compared against
        // the baked <head> already on disk — see `headHonesty.ts`. The LAST
        // description wins because that is what unhead leaves in place after it
        // replaces the baked one; the count is carried so a duplicate is visible.
        const head = await page.evaluate(() => {
          const metas = Array.from(
            document.querySelectorAll('meta[name="description" i]')
          ) as HTMLMetaElement[]
          return {
            title: document.title ?? null,
            description: metas.length ? metas[metas.length - 1].getAttribute('content') : null,
            descriptionCount: metas.length,
          }
        })
        return { bodyHtml: bodyHtml ?? '', errors, head }
      } catch (err) {
        // A nav/mount timeout is itself a fatal render failure — report it so the
        // caller fails the build loud rather than shipping an empty body.
        errors.push(err instanceof Error ? err.message : String(err))
        return { bodyHtml: '', errors }
      } finally {
        await page.close()
      }
    },
  }

  // C-488: `close()` is called from a `finally` (see `runBodyPrerender`), and a
  // caller that also closes defensively — or an error path and a normal path
  // that both reach the same `finally` — can call it twice. Memoise the teardown
  // so it is idempotent: the second caller awaits the SAME settled promise
  // instead of re-entering `context.close()` / `browser.close()` on already-torn-
  // down handles and racing the first teardown that is still in flight. Each step
  // is individually caught, so teardown reports success once everything it owns
  // is down and never surfaces a shutdown error as a build failure.
  let teardown: Promise<void> | null = null

  return {
    renderer,
    close() {
      teardown ??= (async () => {
        try {
          await context.close()
        } catch {
          /* ignore */
        }
        try {
          await browser.close()
        } catch {
          /* ignore */
        }
        try {
          await server.close()
        } catch {
          /* ignore */
        }
      })()
      return teardown
    },
  }
}
